import { cva } from "class-variance-authority";
import type { Mode } from "src/utils/types/mode";
import Animal from "src/design-system/icons/transit/Animal.svg?react";
import Bike from "src/design-system/icons/transit/Bike.svg?react";
import Bus from "src/design-system/icons/transit/Bus.svg?react";
import NightBus from "src/design-system/icons/transit/BusNight.svg?react";
import NightTrain from "src/design-system/icons/transit/TrainNight.svg?react";
import Subway from "src/design-system/icons/transit/Subway.svg?react";
import BusFerry from "src/design-system/icons/transit/BusFerry.svg?react";
import CableCar from "src/design-system/icons/transit/CableCar.svg?react";
import Car from "src/design-system/icons/transit/Car.svg?react";
import CarFerry from "src/design-system/icons/transit/CarFerry.svg?react";
import Circle from "src/svg/Circle.svg?react";
import Ferry from "src/design-system/icons/transit/Ferry.svg?react";
import Helicopter from "src/design-system/icons/transit/Helicopter.svg?react";
import Plane from "src/design-system/icons/transit/Plane.svg?react";
import Rideshare from "src/design-system/icons/transit/Rideshare.svg?react";
import Taxi from "src/design-system/icons/transit/Taxi.svg?react";
import Towncar from "src/design-system/icons/transit/Towncar.svg?react";
import Train from "src/design-system/icons/transit/Train.svg?react";
import Tram from "src/design-system/icons/transit/Tram.svg?react";
import Unknown from "src/design-system/icons/transit/Unknown.svg?react";
import Walk from "src/design-system/icons/transit/Walk.svg?react";
import BusMultiple from "src/svg/transit/BusMultiple.svg?react";
import PlaneMultiple from "src/svg/transit/PlaneMultiple.svg?react";
import CableCarMultiple from "src/svg/transit/CableCarMultiple.svg?react";
import CarMultiple from "src/svg/transit/CarMultiple.svg?react";
import FerryMultiple from "src/svg/transit/FerryMultiple.svg?react";
import HelicopterMultiple from "src/svg/transit/HelicopterMultiple.svg?react";
import RideshareMultiple from "src/svg/transit/RideshareMultiple.svg?react";
import TaxiMultiple from "src/svg/transit/TaxiMultiple.svg?react";
import TrainMultiple from "src/svg/transit/TrainMultiple.svg?react";
import TramMultiple from "src/svg/transit/TramMultiple.svg?react";
import { tw } from "src/design-system/tw";
import type { SVGProps } from "react";

const transitIconVariants = cva("", {
  variants: {
    mode: {
      animal: "text-transport-animal",
      bike: "text-transport-bike",
      bus: "text-transport-bus",
      cablecar: "text-transport-cablecar",
      car: "text-transport-car",
      ferry: "text-transport-ferry",
      helicopter: "text-transport-helicopter",
      nightbus: "text-transparent",
      nighttrain: "text-transparent",
      plane: "text-transport-plane",
      rideshare: "text-transport-rideshare",
      shuttle: "text-transport-shuttle",
      subway: "text-transport-subway",
      taxi: "text-transport-taxi",
      train: "text-transport-train",
      tram: "text-transport-tram",
      towncar: "text-transport-towncar",
      unknown: "text-icon-primary",
      walk: "text-transport-walk",
      busferry: "text-transparent",
      carferry: "text-transparent",
      multi: "text-transport-multi",
    },
  },
});

function isMultipleMode(mode: Mode): mode is keyof typeof transitMultipleIcons {
  return mode in transitMultipleIcons;
}

export function TransitIcon({
  mode,
  className,
  isMultiple,
  ...svgProps
}: {
  mode: Mode;
  className?: string;
  isMultiple?: boolean;
} & Omit<SVGProps<SVGSVGElement>, "color">) {
  const iconClass = transitIconVariants({ mode });

  if (isMultiple && isMultipleMode(mode)) {
    const Icon = transitMultipleIcons[mode];
    return <Icon {...svgProps} className={tw(iconClass, className)} />;
  }

  const Icon = transitIcons[mode];
  return <Icon {...svgProps} className={tw(iconClass, className)} />;
}

const transitIcons = {
  animal: Animal,
  bike: Bike,
  bus: Bus,
  busferry: BusFerry,
  nightbus: NightBus,
  cablecar: CableCar,
  car: Car,
  carferry: CarFerry,
  ferry: Ferry,
  helicopter: Helicopter,
  multi: Circle,
  plane: Plane,
  rideshare: Rideshare,
  shuttle: Bus,
  taxi: Taxi,
  towncar: Towncar,
  train: Train,
  nighttrain: NightTrain,
  subway: Subway,
  tram: Tram,
  unknown: Unknown,
  walk: Walk,
};

const transitMultipleIcons = {
  bus: BusMultiple,
  cablecar: CableCarMultiple,
  car: CarMultiple,
  ferry: FerryMultiple,
  helicopter: HelicopterMultiple,
  plane: PlaneMultiple,
  rideshare: RideshareMultiple,
  taxi: TaxiMultiple,
  train: TrainMultiple,
  tram: TramMultiple,
};
